import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import { leaderboardDataService } from '../../services/leaderboardDataService';

const LeaderboardDataTest = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  const checkCompatibility = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await leaderboardDataService.checkLeaderboardCompatibility();
      setResults(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const seedTestData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await leaderboardDataService.seedLeaderboardTestData();
      setResults(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fixExistingAchievements = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await leaderboardDataService.fixExistingAchievements();
      setResults(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await leaderboardDataService.checkLeaderboardData();
      setResults(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} mb={3}>
        🏆 Leaderboard Data Management
      </Typography>

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Button
          variant="contained"
          onClick={checkCompatibility}
          disabled={loading}
        >
          Check Compatibility
        </Button>
        <Button
          variant="outlined"
          onClick={fixExistingAchievements}
          disabled={loading}
          color="warning"
        >
          Fix Existing Achievements
        </Button>
        <Button
          variant="outlined"
          onClick={seedTestData}
          disabled={loading}
        >
          Seed Test Data
        </Button>
        <Button
          variant="text"
          onClick={checkData}
          disabled={loading}
        >
          Check Data Status
        </Button>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {results && (
        <Card>
          <CardContent>
            <Typography variant="h6" mb={2}>
              Results
            </Typography>
            
            {results.compatible && (
              <Box mb={2}>
                <Typography variant="subtitle2" color="success.main">
                  Compatible ({results.compatible.length}):
                </Typography>
                <List dense>
                  {results.compatible.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={item} />
                      <Chip label="Ready" color="success" size="small" />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            
            {results.missing && (
              <Box mb={2}>
                <Typography variant="subtitle2" color="error.main">
                  Missing ({results.missing.length}):
                </Typography>
                <List dense>
                  {results.missing.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={item} />
                      <Chip label="Missing" color="error" size="small" />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            
            {results.fixed && (
              <Box mb={2}>
                <Typography variant="subtitle2" color="warning.main">
                  Fixed ({results.fixed.length}):
                </Typography>
                <List dense>
                  {results.fixed.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={item} />
                      <Chip label="Fixed" color="warning" size="small" />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {results.skipped && (
              <Box mb={2}>
                <Typography variant="subtitle2" color="info.main">
                  Skipped ({results.skipped.length}):
                </Typography>
                <List dense>
                  {results.skipped.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={item} />
                      <Chip label="Exists" color="info" size="small" />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {results.errors && results.errors.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" color="error.main">
                  Errors ({results.errors.length}):
                </Typography>
                <List dense>
                  {results.errors.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText 
                        primary={item.name || item.userId} 
                        secondary={item.error}
                      />
                      <Chip label="Error" color="error" size="small" />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {results.usersProcessed && (
              <Box>
                <Typography variant="body2">
                  Users Processed: {results.usersProcessed}
                </Typography>
                <Typography variant="body2">
                  Achievements Created: {results.achievementsCreated}
                </Typography>
              </Box>
            )}
            
            {results.total && (
              <Box>
                <Typography variant="body2">
                  Total Achievements: {results.total}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Required Achievement Types for Leaderboards
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="EARLY_DISCOVERIES" 
                secondary="For Hype Hunters leaderboard"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="REVIEW_COUNT" 
                secondary="For Top Reviewers leaderboard"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="FOLLOWER_COUNT" 
                secondary="For Social Butterflies leaderboard"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="MOVIE_WATCHED_COUNT" 
                secondary="For Binge Watchers leaderboard"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="LOGIN_STREAK" 
                secondary="For Streak Masters leaderboard"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LeaderboardDataTest;